import React, { useState } from 'react';
import { Button } from 'rsuite';
import { useStoreState } from '../../store/hooks';
import { switchToGlobalStock } from '../../api/partner/stock/stock';
import handleError from '../../api/apiErrorHandle';
import { notification, notificationSuccess } from '../../services/notificationActions';
import Alert from '../Alert';
import { Permission } from '../../store/user/types';

const CONFIRMATION = 'I do confirm I want to set up “null” value for all ONHAND stocks.';

const PROPAGATION_NOTE = 'Offers whose ONHAND stock differs from the GLOBAL stock of their product will be sent '
  + 'to the marketplace with the GLOBAL stock.';

const RESULT_DURATION_UNTIL_DISMISSED = 0;

export default function PartnerSetupGlobalStockSwitch(): JSX.Element {
  const currentUser = useStoreState(state => state.user);
  const hasPermission = currentUser.hasPermission(Permission.GlobalStockSwitch);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [switching, setSwitching] = useState(false);

  async function handleSwitch(): Promise<void> {
    if (switching) {
      return;
    }

    setSwitching(true);
    try {
      const response = await switchToGlobalStock();
      const { clearedOfferStocks, offersMarkedForUpdate, failedProducts } = response.data;
      const result = `Cleared ${clearedOfferStocks} ONHAND stocks, marked ${offersMarkedForUpdate} offers for `
        + `a marketplace update, failed to switch ${failedProducts} products.`;

      if (failedProducts > 0) {
        notification(result, 'warning', RESULT_DURATION_UNTIL_DISMISSED);
        return;
      }

      notificationSuccess(result, RESULT_DURATION_UNTIL_DISMISSED);
    } catch (error) {
      handleError(error);
    } finally {
      setSwitching(false);
      setConfirmOpen(false);
    }
  }

  return (
    <>
      <Button
        appearance="ghost"
        disabled={!hasPermission || switching}
        onClick={() => setConfirmOpen(true)}
      >
        Switch all offers to GLOBAL stock
      </Button>

      <Alert
        isOpen={confirmOpen}
        isLoading={switching}
        onConfirm={() => handleSwitch()}
        onClose={() => setConfirmOpen(false)}
        title="Confirmation"
        confirmLabel="I do confirm"
      >
        <>
          <p>{CONFIRMATION}</p>
          <p>{PROPAGATION_NOTE}</p>
        </>
      </Alert>
    </>
  );
}
